import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  FormFeedback,
  Spinner,
} from "reactstrap";
import { Btn } from "../../../AbstractElements";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios, { AxiosError } from "axios";
import { baseUrl, mediaUrl, uploadFile } from "../../../Utils/FileUpload";
import {
  Submit,
  AddVideoPackageTitle,
  PackageTitle,
} from "../../../Utils/Constants";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";
import useCustomToasts from "../../../hooks/useCustomToast";

interface FormValues {
  name?: string;
  image?: FileList | null; // add optional marker to allow undefined
  amount: number;
  country_id: number;
  base_customer_credit_point: number;
  base_video_call_duration: number;
  base_agent_payment: number;
  base_agent_point: number;
  description: string;
}

interface ErrorResponse {
  message: string;
}

export default function AddVideoPackage() {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormValues>();
  const navigate = useNavigate();
  const { SuccessToast, ErrorToast } = useCustomToasts(navigate);
  const [countries, setCountries] = useState<{ id: number; name: string }[]>(
    []
  );
  const [existingFile, setExistingFile] = useState<string | null>(null);

  const [baseValues, setBaseValues] = useState({
    base_amount: 0,
    base_customer_credit_point: 0,
    base_video_call_duration: 0,
    base_agent_point: 0,
    base_agent_payment: 0,
  });
  const amount = watch("amount");
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const token = localStorage.getItem("token");

        const [countryRes, packageSettingRes] = await Promise.all([
          axios.get(`${baseUrl}country`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${baseUrl}video_package_payment_setting/getall`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setCountries(countryRes.data);

        if (packageSettingRes.data && packageSettingRes.data.length > 0) {
          const baseData = packageSettingRes.data[0];
          setBaseValues({
            base_amount: parseFloat(baseData.base_amount),
            base_customer_credit_point: parseFloat(
              baseData.base_customer_credit_point
            ),
            base_video_call_duration: parseFloat(baseData.base_video_call_duration),
            base_agent_point: parseFloat(baseData.base_agent_point),
            base_agent_payment: parseFloat(baseData.base_agent_payment),
          });
        }
      } catch (error) {
        console.error("Failed to fetch countries or base values", error);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const inputAmount = parseFloat(String(amount || 0));
    const baseAmount = parseFloat(String(baseValues.base_amount));

    if (inputAmount && baseAmount) {
      setValue(
        "base_customer_credit_point",
        parseFloat(
          (
            (inputAmount / baseAmount) *
            baseValues.base_customer_credit_point
          ).toFixed(2)
        )
      );
      setValue(
        "base_video_call_duration",
        parseFloat(
          ((inputAmount / baseAmount) * baseValues.base_video_call_duration).toFixed(
            2
          )
        )
      );
      setValue(
        "base_agent_payment",
        parseFloat(
          ((inputAmount / baseAmount) * baseValues.base_agent_payment).toFixed(
            2
          )
        )
      );
      setValue(
        "base_agent_point",
        parseFloat(
          ((inputAmount / baseAmount) * baseValues.base_agent_point).toFixed(2)
        )
      );
    } else {
      setValue("base_customer_credit_point", 0);
      setValue("base_video_call_duration", 0);
      setValue("base_agent_payment", 0);
      setValue("base_agent_point", 0);
    }
  }, [amount, baseValues, setValue]);

  const onSubmit = async (data: FormValues) => {
    setIsButtonDisabled(true);

    try {
      const file = data.image?.[0];

      if (!file) {
        toast.error("Please upload a image.");
        return;
      }

      const fileName = await uploadFile(file);

      const payload = {
        name: data.name,
        amount: data.amount,
        image: fileName,
        country_id: data.country_id,
        description: data.description,
      };
      // console.log(payload,"payload");
      const token = localStorage.getItem("token");

      const response = await axios.post(`${baseUrl}video_package/store`, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        SuccessToast("Data added successfully", "/view_video_package");
      } else {
        ErrorToast("Failed to add");
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      const errorMessage =
        (axiosError.response?.data as ErrorResponse)?.message ||
        "An unexpected error occurred";

      console.error("Error:", errorMessage);
      ErrorToast(errorMessage);
    } finally {
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 2000);
    }
  };

  // Prevent form submission on Enter key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };
  const handleAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };
  return (
    <>
      <Breadcrumbs
        pageTitle={AddVideoPackageTitle}
        parent={PackageTitle}
        title={AddVideoPackageTitle}
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody className="custom-input form-validation">
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row className="g-3">
                <Col xs={12}>
                  <Label htmlFor="package-name">Package Name</Label>
                  <Controller
                    name="name"
                    control={control}
                    rules={{
                      required: "Package name is required",
                    }}
                    render={({ field }) => (
                      <>
                        <Input
                          id="package-name"
                          type="text"
                          placeholder="Package Name"
                          {...field}
                          invalid={!!errors.name}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.name ? "red" : "",
                          }}
                          autoComplete="off"
                          onKeyDown={handleKeyDown}
                        />
                        {errors.name && (
                          <FormFeedback>{errors.name.message}</FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="country">Country</Label>
                  <Controller
                    name="country_id"
                    control={control}
                    rules={{ required: "Country is required" }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="select"
                          id="country"
                          {...field}
                          invalid={!!errors.country_id}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.country_id ? "red" : "",
                          }}
                          onKeyDown={handleKeyDown}
                        >
                          <option value="">Select Country</option>
                          {countries.map((country) => (
                            <option key={country.id} value={country.id}>
                              {country.name}
                            </option>
                          ))}
                        </Input>
                        {errors.country_id && (
                          <FormFeedback>
                            {errors.country_id.message}
                          </FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="formFile">Image</Label>
                  <Controller
                    name="image"
                    control={control}
                    rules={{ required: "Image is required" }}
                    render={({ field: { onChange, onBlur, ref } }) => (
                      <>
                        <Input
                          id="formImage"
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              onChange(e.target.files);
                              // Update the image preview immediately
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setExistingFile(reader.result as string);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          onBlur={onBlur}
                          innerRef={ref}
                          invalid={!!errors.image}
                          style={{ borderColor: errors.image ? "red" : "" }}
                          onKeyDown={handleKeyDown}
                        />
                        {errors.image && (
                          <FormFeedback>{errors.image.message}</FormFeedback>
                        )}
                        {existingFile && (
                          <img
                            src={
                              existingFile.startsWith("data:")
                                ? existingFile
                                : `${mediaUrl}/${existingFile}`
                            }
                            alt="Preview"
                            width="70"
                            height="70"
                            style={{ marginTop: "5px", borderRadius: "10px" }}
                          />
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="description">Description</Label>
                  <Controller
                    name="description"
                    control={control}
                    rules={{
                      required: "Description is required",
                    }}
                    render={({ field }) => (
                      <>
                        <Input
                          id="description"
                          type="textarea"
                          placeholder="Description"
                          {...field}
                          invalid={!!errors.description}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.description ? "red" : "",
                          }}
                          autoComplete="off"
                          onKeyDown={handleKeyDown}
                        />
                        {errors.description && (
                          <FormFeedback>
                            {errors.description.message}
                          </FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="address">Recharge Amount</Label>
                  <Controller
                    name="amount"
                    control={control}
                    rules={{ required: "Amount is required" }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="number"
                          id="amount"
                          placeholder="Amount"
                          {...field}
                          invalid={!!errors.amount}
                          autoComplete="off"
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.amount ? "red" : "",
                          }}
                          onKeyDown={(e) => {
                            handleKeyDown(e);
                            handleAmountKeyDown(e);
                          }}
                          onWheel={(e) => (e.target as HTMLInputElement).blur()}
                        />
                        {errors.amount && (
                          <FormFeedback>{errors.amount.message}</FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="address">Customer Points</Label>
                  <Controller
                    name="base_customer_credit_point"
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        step="0.01"
                        id="base_customer_credit_point"
                        placeholder="Customer Credit Point"
                        {...field}
                        disabled
                      />
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="address">Video Call Duration (mins)</Label>
                  <Controller
                    name="base_video_call_duration"
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        step="0.01"
                        id="base_video_call_duration"
                        placeholder="Call Duration"
                        {...field}
                        disabled
                      />
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="address">Agent Points</Label>
                  <Controller
                    name="base_agent_point"
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        step="0.01"
                        id="base_agent_point"
                        placeholder="Agent's Credit"
                        {...field}
                        disabled
                      />
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="address">Agent Payment</Label>
                  <Controller
                    name="base_agent_payment"
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        step="0.01"
                        id="base_agent_payment"
                        placeholder="Agent's Payment"
                        {...field}
                        disabled
                      />
                    )}
                  />
                </Col>

                <Col xs={12} className="mt-2">
                  <Btn color="primary" disabled={isButtonDisabled}>
                    {Submit}
                  </Btn>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
      <ToastContainer
        toastStyle={{
          userSelect: "none",
        }}
      />
    </>
  );
}
